// Phase 4 FE URL cache for the WS redirect protocol. On receipt of a
// SessionMovedFrame, the WS manager calls storeAffinity() with the target
// URL. On the next connection attempt getAffinityURL() returns the cached
// target (if non-expired) instead of the default URL, eliminating the
// redirect round-trip after the first hit.
//
// The storage key uses the lvdr_session_ prefix so hardLogout() auto-wipes
// it via its prefix-scan (web/src/lib/auth/hardLogout.ts).

const STORAGE_KEY = 'lvdr_session_ws_affinity';
const DEFAULT_TTL_MS = 15 * 60 * 1000;

interface AffinityRecord {
  target: string;
  cachedAt: number;
  expiresAt: number;
}

export function storeAffinity(target: string, ttlMs: number = DEFAULT_TTL_MS): void {
  if (!target) {
    clearAffinity();
    return;
  }
  const now = Date.now();
  const record: AffinityRecord = {
    target,
    cachedAt: now,
    expiresAt: now + ttlMs,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // localStorage unavailable (private-mode quota, SSR, etc.) — ignore;
    // we'll just pay the redirect cost on next connect.
  }
}

export function getAffinityURL(): string | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const record = JSON.parse(raw) as AffinityRecord;
    if (!record || typeof record.target !== 'string' || typeof record.expiresAt !== 'number') {
      clearAffinity();
      return null;
    }
    if (Date.now() > record.expiresAt) {
      clearAffinity();
      return null;
    }
    return record.target;
  } catch {
    clearAffinity();
    return null;
  }
}

export function clearAffinity(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
